import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import SEO from "@/components/SEO";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowRight,
  BrainCircuit,
  GraduationCap,
  Handshake,
  Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";

import { educators, type Educator } from "@/data/educators";

const educatorSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "Forward-Deployed Educators",
  description:
    "Meet the facilitators, trainers, and coaches who lead Tech Leaders AI programs.",
  mainEntity: {
    "@type": "ItemList",
    itemListElement: educators.map((educator, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Person",
        name: educator.name,
        ...(educator.isPlaceholder ? {} : { jobTitle: educator.role }),
      },
    })),
  },
};

const deliveryModes = [
  {
    title: "Facilitate",
    description:
      "Turn a room of competing priorities into shared direction, useful decisions, and a clear next move.",
    icon: Handshake,
  },
  {
    title: "Train",
    description:
      "Build practical AI skills through hands-on work with the tools, workflows, and constraints teams actually face.",
    icon: GraduationCap,
  },
  {
    title: "Coach",
    description:
      "Help leaders apply what they learned, work through obstacles, and make new ways of working stick.",
    icon: BrainCircuit,
  },
];

const EducatorCard = ({ educator }: { educator: Educator }) => (
  <Card
    className={`h-full overflow-hidden border-border/70 shadow-sm transition-shadow hover:shadow-lg ${
      educator.isPlaceholder ? "border-dashed bg-muted/30" : "bg-card"
    }`}
  >
    <CardContent className="flex h-full flex-col p-6 sm:p-8">
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        {educator.image ? (
          <img
            src={educator.image}
            alt={educator.name}
            className="h-28 w-28 shrink-0 rounded-2xl object-cover shadow-md"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div
            className="flex h-28 w-28 shrink-0 items-center justify-center rounded-2xl border border-dashed border-primary/40 bg-primary/10 text-3xl font-bold text-primary"
            aria-label={`${educator.name} headshot placeholder`}
          >
            {educator.initials}
          </div>
        )}

        <div className="min-w-0">
          {educator.isPlaceholder ? (
            <div className="mb-3 flex flex-wrap gap-2">
              <Badge variant="outline">Profile coming soon</Badge>
            </div>
          ) : null}
          <h3 className="text-2xl font-bold tracking-tight text-foreground">
            {educator.name}
          </h3>
          <p className="mt-2 text-sm font-medium leading-relaxed text-primary">
            {educator.role}
          </p>
        </div>
      </div>

      <div className="mt-6 space-y-4 text-base leading-7 text-muted-foreground">
        {educator.bio.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
    </CardContent>
  </Card>
);

const ForwardDeployedEducators = () => (
  <div className="min-h-screen bg-background">
    <SEO
      title="Forward-Deployed Educators | Technical Leaders"
      description="Meet the facilitators, trainers, and coaches who help Tech Leaders program participants turn AI learning into practical ways of working."
      keywords={[
        "AI facilitators",
        "AI trainers",
        "AI coaches",
        "forward-deployed educators",
        "enterprise AI training",
        "AI leadership coaching",
      ]}
      structuredData={educatorSchema}
    />
    <Navigation />

    <main>
      {/* Hero — same background-image treatment as the home page hero */}
      <section className="relative overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/ai-in-ar.png"
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/85 to-background/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 z-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />

        <div className="relative z-10 mx-auto max-w-5xl px-4 pb-20 pt-32 text-center sm:px-6 lg:px-8">
          <Badge variant="outline" className="mb-6 border-orange-500/40 bg-background/80 px-4 py-2 text-sm">
            <Sparkles className="mr-2 h-4 w-4 text-orange-500" aria-hidden="true" />
            Tech Leaders AI programs
          </Badge>
          <h1 className="text-balance font-bold leading-tight tracking-tight">
            <span className="block bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-4xl text-transparent sm:text-5xl lg:text-6xl">
              Forward-Deployed Educators
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-3xl text-xl leading-8 text-foreground/70 sm:text-2xl">
            AI facilitators, trainers, and coaches who work alongside your team.
          </p>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-foreground/70 sm:text-lg">
            Our educators bring practical experience into the room, connect learning to real work, and help teams turn new AI capabilities into repeatable ways of working.
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button
              asChild
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700"
            >
              <Link to="/ai-call">
                Book a Strategy Session
                <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="border-y border-border/70 bg-muted/30 px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid gap-6 md:grid-cols-3">
            {deliveryModes.map((mode) => (
              <div key={mode.title} className="rounded-2xl border border-border/70 bg-background p-6">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <mode.icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <h2 className="text-xl font-bold text-foreground">{mode.title}</h2>
                <p className="mt-2 leading-7 text-muted-foreground">{mode.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-6 lg:px-8" aria-labelledby="educators-heading">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto mb-12 max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">The educator team</p>
            <h2 id="educators-heading" className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Practitioners who help teams learn by doing
            </h2>
          </div>

          <div className="grid items-stretch gap-8 lg:grid-cols-2">
            {educators.map((educator) => (
              <EducatorCard key={educator.name} educator={educator} />
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl border border-orange-500/30 bg-gradient-to-br from-orange-500/15 via-card to-blue-500/10 p-8 text-center sm:p-12">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            Bring the right educator into the work
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-muted-foreground">
            Tell us what your team is trying to change, and we’ll help shape the right mix of facilitation, training, and coaching.
          </p>
          <Button asChild size="lg" className="mt-8 bg-gradient-to-r from-orange-500 to-red-600 text-white hover:from-orange-600 hover:to-red-700">
            <Link to="/ai-call">
              Talk with Tech Leaders
              <ArrowRight aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </section>
    </main>

    <Footer />
  </div>
);

export default ForwardDeployedEducators;
